import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, FileText, TrendingUp, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

const AssessmentResponse = () => {
  const { responseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchResponse();
  }, [responseId]);

  const fetchResponse = async () => {
    try {
      console.log('Fetching assessment response for ID:', responseId);
      const response = await api.get(`/assessments/response/${responseId}`);
      console.log('Assessment response data:', response.data);
      setResponse(response.data.response);
    } catch (error) {
      console.error('Failed to fetch assessment response:', error);
      console.error('Error details:', error.response?.data);
      setError('Failed to load assessment response');
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-500">{error}</p>
          <button
            onClick={() => navigate('/app/assessments')}
            className="btn-primary mt-4"
          >
            Back to Assessments
          </button>
        </div>
      </div>
    );
  }

  if (!response) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/app/assessments')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Assessments</span>
        </button>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span>Completed {new Date(response.completedAt).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Assessment Info */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{response.assessmentName}</h1>
        {location.state?.therapistName && (
          <p className="text-gray-600">by {location.state.therapistName}</p>
        )}
        <div className="flex items-center justify-center space-x-4 mt-2 text-sm text-gray-500">
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>{response.duration} minutes</span>
          </div>
          <div className="flex items-center space-x-1">
            <FileText className="w-4 h-4" />
            <span>{response.responses.length} questions</span>
          </div>
        </div>
      </div>

      {/* Insights Section */}
      {response.insights && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Assessment Insights</h2>
            {response.insights.aiGenerated && (
              <div className="flex items-center space-x-2 text-sm text-blue-600">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span>AI Generated</span>
              </div>
            )}
          </div>
          
          {/* Risk Level */}
          {response.insights.riskLevel && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Risk Level</h3>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(response.insights.riskLevel)}`}>
                {response.insights.riskLevel.charAt(0).toUpperCase() + response.insights.riskLevel.slice(1)} Risk
              </span>
              {response.insights.riskLevelExplanation && (
                <p className="text-gray-700 mt-2 text-sm leading-relaxed">
                  {response.insights.riskLevelExplanation}
                </p>
              )}
            </div>
          )}

          {/* Key Findings */}
          {response.insights.keyFindings && response.insights.keyFindings.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Key Findings</h3>
              <ul className="space-y-2">
                {response.insights.keyFindings.map((finding, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <TrendingUp className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{finding}</span>
                  </li>
                ))}
              </ul>
              {response.insights.keyFindingsExplanation && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Analysis:</strong> {response.insights.keyFindingsExplanation}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Recommendations */}
          {response.insights.recommendations && response.insights.recommendations.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Recommendations</h3>
              <ul className="space-y-2">
                {response.insights.recommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{recommendation}</span>
                  </li>
                ))}
              </ul>
              {response.insights.recommendationsExplanation && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Guidance:</strong> {response.insights.recommendationsExplanation}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Responses Section */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Responses</h2>
        <div className="space-y-6">
          {response.responses.map((item, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-gray-900">Question {index + 1}</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {item.category}
                </span>
              </div>
              <p className="text-gray-700 mb-3">{item.question}</p>
              <div className="bg-primary-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-primary-900 mb-1">Your Answer:</p>
                <p className="text-primary-800">
                  {Array.isArray(item.answer) ? item.answer.join(', ') : item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4">
        <button
          onClick={() => navigate('/app/assessments')}
          className="btn-outline"
        >
          Back to Assessments
        </button>
        <button
          onClick={() => navigate(`/app/chat/${response.therapistId}`)}
          className="btn-primary"
        >
          Chat with Therapist
        </button>
      </div>
    </div>
  );
};

export default AssessmentResponse; 